import type { HandbookChangeRequest, HandbookChangeRequestStatus, HandbookPolicy } from '../types/handbookPolicy';
import { bumpHandbookVersion } from './handbookPolicyAccess';

export type HandbookCrActor = {
  isAuthor: boolean;
  isCeo: boolean;
};

export interface HandbookMergeResult {
  nextVersion: string;
  title: string;
  effectiveDate: string;
  location: string;
  summary?: string;
  body: string;
  bodyFormat: HandbookPolicy['bodyFormat'];
}

/** Normalize published `currentVersion` into a snapshot label (`1.0` when missing or non-numeric). */
export function handbookSnapshotVersionLabel(current: string | undefined): string {
  const t = String(current || '')
    .trim()
    .replace(/^v/i, '');
  if (!t) return '1.0';
  if (/^\d+(\.\d+)*$/.test(t)) return t;
  return '1.0';
}

export function handbookVersionDocId(version: string): string {
  const label = handbookSnapshotVersionLabel(version);
  return `v${label.replace(/\./g, '_')}`;
}

export function canTransitionHandbookChangeRequest(
  from: HandbookChangeRequestStatus,
  to: HandbookChangeRequestStatus,
  actor: HandbookCrActor,
): boolean {
  if (from === to) return true;
  if (to === 'open') {
    return actor.isAuthor && (from === 'draft' || from === 'changes_requested');
  }
  if (to === 'withdrawn') {
    return actor.isAuthor && (from === 'draft' || from === 'open' || from === 'changes_requested');
  }
  if (to === 'changes_requested') {
    return actor.isCeo && from === 'open';
  }
  if (to === 'rejected') {
    return actor.isCeo && from === 'open';
  }
  if (to === 'approved') {
    if (!actor.isCeo) return false;
    if (from === 'open') return true;
    // CEO may merge their own draft in one step.
    return actor.isAuthor && (from === 'draft' || from === 'changes_requested');
  }
  if (to === 'draft') {
    return actor.isAuthor && from === 'draft';
  }
  return false;
}

export function applyHandbookMerge(policy: HandbookPolicy, cr: HandbookChangeRequest): HandbookMergeResult {
  if (cr.status !== 'open' && cr.status !== 'draft' && cr.status !== 'changes_requested') {
    throw new Error(`Cannot merge a change request in status “${cr.status}”.`);
  }
  const base = handbookSnapshotVersionLabel(policy.currentVersion || cr.baseVersion);
  const nextVersion = policy.currentVersion?.trim()
    ? bumpHandbookVersion(policy.currentVersion)
    : bumpHandbookVersion(base);
  return {
    nextVersion,
    title: cr.title.trim(),
    effectiveDate: cr.effectiveDate.trim(),
    location: cr.location.trim(),
    summary: cr.summary?.trim() || undefined,
    body: cr.body || '',
    bodyFormat: cr.bodyFormat === 'plain' ? 'plain' : 'html',
  };
}

export function handbookChangeRequestDraftFromPolicy(
  policy: HandbookPolicy,
): Pick<HandbookChangeRequest, 'title' | 'effectiveDate' | 'location' | 'summary' | 'bodyFormat' | 'body' | 'baseVersion'> {
  return {
    title: policy.title,
    effectiveDate: policy.effectiveDate,
    location: policy.location,
    summary: policy.summary,
    bodyFormat: policy.bodyFormat,
    body: policy.body,
    baseVersion: handbookSnapshotVersionLabel(policy.currentVersion),
  };
}
